import type { EmbeddedForm } from "@/generated/sb";
import { Input } from "@repo/ui-parkmobile/Input";
import { Textarea } from "@repo/ui-parkmobile/Textarea";
import { Button } from "@repo/ui-parkmobile/Button";
import { Select } from "@repo/ui-parkmobile/Select";
import { cn } from "@repo/utils/cn";
import { STATE_PROVINCE_OPTIONS } from "@/constants/stateProvinceOptions";
import {
  trackFormSubmit,
  trackFormStart,
  trackFormInputClick,
  trackFormError,
  type FormErrorType,
} from "@/utils/trackingScripts";
import { useRef, useState, useEffect } from "react";

export default function SalesforceForm({
  type,
  sectionName,
}: {
  type: EmbeddedForm["salesforceFormType"];
  sectionName?: string;
}) {
  switch (type) {
    case "book-demo":
      return <BookDemoForm sectionName={sectionName} />;

    case "contact-form":
      return <ContactForm sectionName={sectionName} />;

    case "get-demo-form":
      return <GetDemoForm sectionName={sectionName} />;
  }
}

function BookDemoForm({ sectionName }: { sectionName?: string }) {
  const formStartTracked = useRef(false);
  const ignoreNextFocus = useRef(false);
  const [countryCode, setCountryCode] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [industry, setIndustry] = useState("");
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string | null>>(
    {},
  );

  useEffect(() => {
    setCountryCode("");
    setStateCode("");
    setIndustry("");
    setFormValues({});
    setFormErrors({});
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        ignoreNextFocus.current = true;
        setTimeout(() => {
          ignoreNextFocus.current = false;
        }, 100);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  const validateField = (fieldName: string, value: string): string | null => {
    if (
      ["first_name", "last_name", "email", "phone", "company"].includes(
        fieldName,
      ) ||
      fieldName === "country_code" ||
      fieldName === "state_code" ||
      fieldName === "industry"
    ) {
      if (!value || value.trim() === "") {
        return "This field is required";
      }
    }

    if (fieldName === "email" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return "Please enter a valid email address";
      }
    }

    if (fieldName === "phone" && value) {
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(value) || value.replace(/\D/g, "").length < 10) {
        return "Please enter a valid phone number";
      }
    }

    return null;
  };

  const handleFieldChange = (fieldName: string, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    if (formErrors[fieldName]) {
      setFormErrors((prev) => ({
        ...prev,
        [fieldName]: null,
      }));
    }
  };

  const handleFieldBlur = (fieldName: string, value: string) => {
    const error = validateField(fieldName, value);
    setFormErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  const handleFieldFocus = (fieldName: string) => {
    if (ignoreNextFocus.current) {
      return;
    }

    if (!formStartTracked.current) {
      trackFormStart({
        section: sectionName || "Book Demo Form",
        config: "salesforceForm",
        formName: sectionName || "Book Demo Form",
      });
      formStartTracked.current = true;
    }

    trackFormInputClick({
      section: sectionName || "Book Demo Form",
      config: "salesforceForm",
      formName: sectionName || "Book Demo Form",
      fieldName,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const errors: Record<string, string | null> = {};
    let hasErrors = false;

    const fieldsToValidate = [
      "first_name",
      "last_name",
      "email",
      "phone",
      "company",
      "country_code",
      "state_code",
      "industry",
    ];

    fieldsToValidate.forEach((fieldName) => {
      const value =
        fieldName === "country_code"
          ? countryCode
          : fieldName === "state_code"
            ? stateCode
            : fieldName === "industry"
              ? industry
              : formValues[fieldName] || "";
      const error = validateField(fieldName, value);
      if (error) {
        errors[fieldName] = error;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      e.preventDefault();
      setFormErrors(errors);

      const errorEntries = Object.entries(errors).filter(
        ([, msg]) => msg !== null,
      );
      if (errorEntries.length > 0) {
        const [firstFieldName, firstErrorMessage] = errorEntries[0]!;
        const errorType: FormErrorType = firstErrorMessage!.includes("required")
          ? "missing_field"
          : "validation_error";

        trackFormError({
          section: sectionName || "Book Demo Form",
          config: "salesforceForm",
          formName: sectionName || "Book Demo Form",
          errorType,
          errorMessage:
            errorEntries.length > 1
              ? `${errorEntries.length} fields have errors. First: ${firstFieldName} - ${firstErrorMessage}`
              : `${firstFieldName}: ${firstErrorMessage}`,
          fieldName: firstFieldName,
        });
      }
      return;
    }

    const formData = new FormData(e.currentTarget);
    const data: Record<string, string> = {};
    formData.forEach((value, key) => {
      data[key] = value.toString();
    });

    trackFormSubmit({
      section: sectionName || "Book Demo Form",
      formName: sectionName || "Book Demo Form",
      formValue: 1,
      config: "salesforceForm",
      status: "submitted",
      industry: data.industry,
      country: data.country_code,
      state: data.state_code,
    });
  };

  return (
    <div className="uipm:bg-[var(--surface-surface-secondary)] uipm:p-6 uipm:md:p-8 uipm:rounded-[var(--radius-radius-lg)]">
      <form
        action={`https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8&orgId=${process.env.NEXT_PUBLIC_SALESFORCE_ORG_ID}`}
        method="POST"
        className="uipm:flex uipm:flex-col uipm:gap-[var(--spacing-primitives-16)] uipm:w-full"
        onSubmit={handleSubmit}
        noValidate
      >
        <input
          name="captcha_settings"
          type="hidden"
          value={`{"keyname":"Recaptcha2025","fallback":"true","orgId":"${process.env.NEXT_PUBLIC_SALESFORCE_ORG_ID}","ts":""}`}
        />
        <input
          name="oid"
          type="hidden"
          value={process.env.NEXT_PUBLIC_SALESFORCE_ORG_ID}
        />
        <input
          name="retURL"
          type="hidden"
          value={`${process.env.NEXT_PUBLIC_SALESFORCE_RETURN_URL}/contact-us/thank-you`}
        />
        <input
          name="debugEmail"
          type="hidden"
          value={process.env.NEXT_PUBLIC_SALESFORCE_DEBUG_EMAIL}
        />
        <input type="hidden" name="lead_source" value="Website - Book a Demo" />

        <div className="uipm:grid uipm:grid-cols-1 uipm:md:grid-cols-2 uipm:gap-[var(--spacing-primitives-16)]">
          <Input
            id="first_name"
            name="first_name"
            type="text"
            placeholder="First Name"
            maxLength={40}
            background="white"
            state={formErrors.first_name ? "error" : "default"}
            value={formValues.first_name || ""}
            onChange={(e) => handleFieldChange("first_name", e.target.value)}
            onFocus={() => handleFieldFocus("first_name")}
          />
          <Input
            id="last_name"
            name="last_name"
            type="text"
            placeholder="Last Name"
            maxLength={80}
            background="white"
            state={formErrors.last_name ? "error" : "default"}
            value={formValues.last_name || ""}
            onChange={(e) => handleFieldChange("last_name", e.target.value)}
            onFocus={() => handleFieldFocus("last_name")}
          />
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="Email"
            maxLength={80}
            background="white"
            state={formErrors.email ? "error" : "default"}
            value={formValues.email || ""}
            onChange={(e) => handleFieldChange("email", e.target.value)}
            onFocus={() => handleFieldFocus("email")}
          />
          <Input
            id="phone"
            name="phone"
            type="tel"
            placeholder="Phone"
            maxLength={40}
            background="white"
            state={formErrors.phone ? "error" : "default"}
            value={formValues.phone || ""}
            onChange={(e) => handleFieldChange("phone", e.target.value)}
            onFocus={() => handleFieldFocus("phone")}
          />
        </div>

        <div className="uipm:w-full">
          <Input
            id="company"
            name="company"
            type="text"
            placeholder="Company"
            maxLength={40}
            background="white"
            state={formErrors.company ? "error" : "default"}
            value={formValues.company || ""}
            onChange={(e) => handleFieldChange("company", e.target.value)}
            onFocus={() => handleFieldFocus("company")}
          />
        </div>

        <div className="uipm:grid uipm:grid-cols-1 uipm:md:grid-cols-2 uipm:gap-[var(--spacing-primitives-16)]">
          <div
            className="uipm:w-full"
            onFocus={() => handleFieldFocus("country_code")}
          >
            <Select
              placeholder="Country"
              value={countryCode}
              background="white"
              state={formErrors.country_code ? "error" : "default"}
              onValueChange={(value) => {
                setCountryCode(value);
                handleFieldChange("country_code", value);
              }}
            >
              <option value="CA">Canada</option>
              <option value="US">United States</option>
            </Select>
            <input type="hidden" name="country_code" value={countryCode} />
          </div>
          <div
            className="uipm:w-full"
            onFocus={() => handleFieldFocus("state_code")}
          >
            <Select
              placeholder="State/Province"
              value={stateCode}
              background="white"
              state={formErrors.state_code ? "error" : "default"}
              onValueChange={(value) => {
                setStateCode(value);
                handleFieldChange("state_code", value);
              }}
            >
              {STATE_PROVINCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <input type="hidden" name="state_code" value={stateCode} />
          </div>
        </div>

        <div
          className="uipm:w-full"
          onFocus={() => handleFieldFocus("industry")}
        >
          <Select
            placeholder="Industry"
            value={industry}
            background="white"
            state={formErrors.industry ? "error" : "default"}
            onValueChange={(value) => {
              setIndustry(value);
              handleFieldChange("industry", value);
            }}
          >
            <option value="Airport">Airport</option>
            <option value="Commercial">Commercial</option>
            <option value="Education">Education</option>
            <option value="Event">Event</option>
            <option value="Municipal">Municipal</option>
            <option value="Operator">Operator</option>
            <option value="Fleet">Fleet</option>
            <option value="Technology Partner">Technology Partner</option>
          </Select>
          <input type="hidden" name="industry" value={industry} />
        </div>

        <div className="uipm:w-full">
          <Textarea
            id={process.env.NEXT_PUBLIC_SALESFORCE_FIELD_MESSAGE}
            name={process.env.NEXT_PUBLIC_SALESFORCE_FIELD_MESSAGE}
            placeholder="Briefly describe what you'd like to gain from this demo."
            rows={1}
            background="white"
            onFocus={() =>
              handleFieldFocus(
                process.env.NEXT_PUBLIC_SALESFORCE_FIELD_MESSAGE || "message",
              )
            }
          />
        </div>

        {/* <div
          className="g-recaptcha"
          data-sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_KEY}
        ></div> */}

        <Button type="submit" variant="primary" className="uipm:self-start">
          Submit
        </Button>
      </form>
    </div>
  );
}

function ContactForm({ sectionName }: { sectionName?: string }) {
  const formStartTracked = useRef(false);
  const ignoreNextFocus = useRef(false);
  const [countryCode, setCountryCode] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [industry, setIndustry] = useState("");
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string | null>>(
    {},
  );

  useEffect(() => {
    setCountryCode("");
    setStateCode("");
    setIndustry("");
    setFormValues({});
    setFormErrors({});
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        ignoreNextFocus.current = true;
        setTimeout(() => {
          ignoreNextFocus.current = false;
        }, 100);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  const validateField = (fieldName: string, value: string): string | null => {
    if (
      ["first_name", "last_name", "email", "phone", "company"].includes(
        fieldName,
      ) ||
      fieldName === "country_code" ||
      fieldName === "state_code" ||
      fieldName === "industry"
    ) {
      if (!value || value.trim() === "") {
        return "This field is required";
      }
    }

    if (fieldName === "email" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return "Please enter a valid email address";
      }
    }

    if (fieldName === "phone" && value) {
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(value) || value.replace(/\D/g, "").length < 10) {
        return "Please enter a valid phone number";
      }
    }

    return null;
  };

  const handleFieldChange = (fieldName: string, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    if (formErrors[fieldName]) {
      setFormErrors((prev) => ({
        ...prev,
        [fieldName]: null,
      }));
    }
  };

  const handleFieldBlur = (fieldName: string, value: string) => {
    const error = validateField(fieldName, value);
    setFormErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  const handleFieldFocus = (fieldName: string) => {
    if (ignoreNextFocus.current) {
      return;
    }

    if (!formStartTracked.current) {
      trackFormStart({
        section: sectionName || "Contact Form",
        config: "salesforceForm",
        formName: sectionName || "Contact Form",
      });
      formStartTracked.current = true;
    }

    trackFormInputClick({
      section: sectionName || "Contact Form",
      config: "salesforceForm",
      formName: sectionName || "Contact Form",
      fieldName,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const errors: Record<string, string | null> = {};
    let hasErrors = false;

    const fieldsToValidate = [
      "first_name",
      "last_name",
      "email",
      "phone",
      "company",
      "country_code",
      "state_code",
      "industry",
    ];

    fieldsToValidate.forEach((fieldName) => {
      const value =
        fieldName === "country_code"
          ? countryCode
          : fieldName === "state_code"
            ? stateCode
            : fieldName === "industry"
              ? industry
              : formValues[fieldName] || "";
      const error = validateField(fieldName, value);
      if (error) {
        errors[fieldName] = error;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      e.preventDefault();
      setFormErrors(errors);

      const errorEntries = Object.entries(errors).filter(
        ([, msg]) => msg !== null,
      );
      if (errorEntries.length > 0) {
        const [firstFieldName, firstErrorMessage] = errorEntries[0]!;
        const errorType: FormErrorType = firstErrorMessage!.includes("required")
          ? "missing_field"
          : "validation_error";

        trackFormError({
          section: sectionName || "Contact Form",
          config: "salesforceForm",
          formName: sectionName || "Contact Form",
          errorType,
          errorMessage:
            errorEntries.length > 1
              ? `${errorEntries.length} fields have errors. First: ${firstFieldName} - ${firstErrorMessage}`
              : `${firstFieldName}: ${firstErrorMessage}`,
          fieldName: firstFieldName,
        });
      }
      return;
    }

    const formData = new FormData(e.currentTarget);
    const data: Record<string, string> = {};
    formData.forEach((value, key) => {
      data[key] = value.toString();
    });

    trackFormSubmit({
      section: sectionName || "Contact Form",
      formName: sectionName || "Contact Form",
      formValue: 1,
      config: "salesforceForm",
      status: "submitted",
      industry: data.industry,
      country: data.country_code,
      state: data.state_code,
    });
  };

  return (
    <div className="uipm:bg-[var(--surface-surface-secondary)] uipm:p-6 uipm:md:p-8 uipm:rounded-[var(--radius-radius-lg)]">
      <form
        action={`https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8&orgId=${process.env.NEXT_PUBLIC_SALESFORCE_ORG_ID}`}
        method="POST"
        className="uipm:flex uipm:flex-col uipm:gap-[var(--spacing-primitives-16)] uipm:w-full"
        onSubmit={handleSubmit}
        noValidate
        autoComplete="off"
      >
        <input
          type="hidden"
          name="oid"
          value={process.env.NEXT_PUBLIC_SALESFORCE_ORG_ID}
        />
        <input
          type="hidden"
          name="retURL"
          value={`${process.env.NEXT_PUBLIC_SALESFORCE_RETURN_URL}/contact-us/thank-you`}
        />
        <input
          type="hidden"
          name="debugEmail"
          value={process.env.NEXT_PUBLIC_SALESFORCE_DEBUG_EMAIL}
        />
        <input type="hidden" name="lead_source" value="Website - Contact Us" />

        <div className="uipm:grid uipm:grid-cols-1 uipm:md:grid-cols-2 uipm:gap-[var(--spacing-primitives-16)]">
          <Input
            id="first_name"
            name="first_name"
            type="text"
            placeholder="First Name*"
            maxLength={40}
            background="white"
            state={formErrors.first_name ? "error" : "default"}
            value={formValues.first_name || ""}
            onChange={(e) => handleFieldChange("first_name", e.target.value)}
            onFocus={() => handleFieldFocus("first_name")}
          />
          <Input
            id="last_name"
            name="last_name"
            type="text"
            placeholder="Last Name*"
            maxLength={80}
            background="white"
            state={formErrors.last_name ? "error" : "default"}
            value={formValues.last_name || ""}
            onChange={(e) => handleFieldChange("last_name", e.target.value)}
            onFocus={() => handleFieldFocus("last_name")}
          />
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="Email*"
            maxLength={80}
            background="white"
            state={formErrors.email ? "error" : "default"}
            value={formValues.email || ""}
            onChange={(e) => handleFieldChange("email", e.target.value)}
            onFocus={() => handleFieldFocus("email")}
          />
          <Input
            id="phone"
            name="phone"
            type="tel"
            placeholder="Phone*"
            maxLength={40}
            background="white"
            state={formErrors.phone ? "error" : "default"}
            value={formValues.phone || ""}
            onChange={(e) => handleFieldChange("phone", e.target.value)}
            onFocus={() => handleFieldFocus("phone")}
          />
        </div>

        <div className="uipm:w-full">
          <Input
            id="company"
            name="company"
            type="text"
            placeholder="Company*"
            maxLength={40}
            background="white"
            state={formErrors.company ? "error" : "default"}
            value={formValues.company || ""}
            onChange={(e) => handleFieldChange("company", e.target.value)}
            onFocus={() => handleFieldFocus("company")}
          />
        </div>

        <div className="uipm:grid uipm:grid-cols-1 uipm:md:grid-cols-2 uipm:gap-[var(--spacing-primitives-16)]">
          <div
            className="uipm:w-full"
            onFocus={() => handleFieldFocus("country_code")}
          >
            <Select
              placeholder="Country"
              value={countryCode}
              background="white"
              state={formErrors.country_code ? "error" : "default"}
              onValueChange={(value) => {
                setCountryCode(value);
                handleFieldChange("country_code", value);
              }}
            >
              <option value="CA">Canada</option>
              <option value="US">United States</option>
            </Select>
            <input type="hidden" name="country_code" value={countryCode} />
          </div>
          <div
            className="uipm:w-full"
            onFocus={() => handleFieldFocus("state_code")}
          >
            <Select
              placeholder="State/Province"
              value={stateCode}
              background="white"
              state={formErrors.state_code ? "error" : "default"}
              onValueChange={(value) => {
                setStateCode(value);
                handleFieldChange("state_code", value);
              }}
            >
              {STATE_PROVINCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <input type="hidden" name="state_code" value={stateCode} />
          </div>
        </div>

        <div
          className="uipm:w-full"
          onFocus={() => handleFieldFocus("industry")}
        >
          <Select
            placeholder="Industry"
            value={industry}
            background="white"
            state={formErrors.industry ? "error" : "default"}
            onValueChange={(value) => {
              setIndustry(value);
              handleFieldChange("industry", value);
            }}
          >
            <option value="Airport">Airport</option>
            <option value="Commercial">Commercial</option>
            <option value="Education">Education</option>
            <option value="Event">Event</option>
            <option value="Municipal">Municipal</option>
            <option value="Operator">Operator</option>
            <option value="Fleet">Fleet</option>
            <option value="Technology Partner">Technology Partner</option>
          </Select>
          <input type="hidden" name="industry" value={industry} />
        </div>

        <div className="uipm:w-full">
          <Textarea
            id={process.env.NEXT_PUBLIC_SALESFORCE_FIELD_MESSAGE}
            name={process.env.NEXT_PUBLIC_SALESFORCE_FIELD_MESSAGE}
            placeholder="How may we help you?"
            rows={1}
            background="white"
            onFocus={() =>
              handleFieldFocus(
                process.env.NEXT_PUBLIC_SALESFORCE_FIELD_MESSAGE || "message",
              )
            }
          />
        </div>

        {/* <div
          className="g-recaptcha"
          data-sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_KEY}
        ></div> */}

        <Button type="submit" variant="primary" className="uipm:self-start">
          Submit
        </Button>
      </form>
    </div>
  );
}

function GetDemoForm({ sectionName }: { sectionName?: string }) {
  const formStartTracked = useRef(false);
  const ignoreNextFocus = useRef(false);
  const [countryCode, setCountryCode] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [industry, setIndustry] = useState("");
  const [product, setProduct] = useState("");
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string | null>>(
    {},
  );

  useEffect(() => {
    setCountryCode("");
    setStateCode("");
    setIndustry("");
    setProduct("");
    setFormValues({});
    setFormErrors({});
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        ignoreNextFocus.current = true;
        setTimeout(() => {
          ignoreNextFocus.current = false;
        }, 100);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  const validateField = (fieldName: string, value: string): string | null => {
    if (
      ["first_name", "last_name", "email", "phone", "company"].includes(
        fieldName,
      ) ||
      fieldName === "country_code" ||
      fieldName === "state_code" ||
      fieldName === "industry"
    ) {
      if (!value || value.trim() === "") {
        return "This field is required";
      }
    }

    if (fieldName === "email" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return "Please enter a valid email address";
      }
    }

    if (fieldName === "phone" && value) {
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(value) || value.replace(/\D/g, "").length < 10) {
        return "Please enter a valid phone number";
      }
    }

    return null;
  };

  const handleFieldChange = (fieldName: string, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    if (formErrors[fieldName]) {
      setFormErrors((prev) => ({
        ...prev,
        [fieldName]: null,
      }));
    }
  };

  const handleFieldBlur = (fieldName: string, value: string) => {
    const error = validateField(fieldName, value);
    setFormErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  const handleFieldFocus = (fieldName: string) => {
    if (ignoreNextFocus.current) {
      return;
    }

    if (!formStartTracked.current) {
      trackFormStart({
        section: sectionName || "Get Demo Form",
        config: "salesforceForm",
        formName: sectionName || "Get Demo Form",
      });
      formStartTracked.current = true;
    }

    trackFormInputClick({
      section: sectionName || "Get Demo Form",
      config: "salesforceForm",
      formName: sectionName || "Get Demo Form",
      fieldName,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const errors: Record<string, string | null> = {};
    let hasErrors = false;

    const fieldsToValidate = [
      "first_name",
      "last_name",
      "email",
      "phone",
      "company",
      "country_code",
      "state_code",
      "industry",
    ];

    fieldsToValidate.forEach((fieldName) => {
      const value =
        fieldName === "country_code"
          ? countryCode
          : fieldName === "state_code"
            ? stateCode
            : fieldName === "industry"
              ? industry
              : formValues[fieldName] || "";
      const error = validateField(fieldName, value);
      if (error) {
        errors[fieldName] = error;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      e.preventDefault();
      setFormErrors(errors);

      const errorEntries = Object.entries(errors).filter(
        ([, msg]) => msg !== null,
      );
      if (errorEntries.length > 0) {
        const [firstFieldName, firstErrorMessage] = errorEntries[0]!;
        const errorType: FormErrorType = firstErrorMessage!.includes("required")
          ? "missing_field"
          : "validation_error";

        trackFormError({
          section: sectionName || "Get Demo Form",
          config: "salesforceForm",
          formName: sectionName || "Get Demo Form",
          errorType,
          errorMessage:
            errorEntries.length > 1
              ? `${errorEntries.length} fields have errors. First: ${firstFieldName} - ${firstErrorMessage}`
              : `${firstFieldName}: ${firstErrorMessage}`,
          fieldName: firstFieldName,
        });
      }
      return;
    }

    const formData = new FormData(e.currentTarget);
    const data: Record<string, string> = {};
    formData.forEach((value, key) => {
      data[key] = value.toString();
    });

    trackFormSubmit({
      section: sectionName || "Get Demo Form",
      formName: sectionName || "Get Demo Form",
      formValue: 1,
      config: "salesforceForm",
      status: "submitted",
      industry: data.industry,
      country: data.country_code,
      state: data.state_code,
    });
  };

  return (
    <div className="uipm:bg-[var(--surface-surface-secondary)] uipm:p-6 uipm:md:p-8 uipm:rounded-[var(--radius-radius-lg)]">
      <form
        action={`https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8&orgId=${process.env.NEXT_PUBLIC_SALESFORCE_ORG_ID}`}
        method="POST"
        className="uipm:flex uipm:flex-col uipm:gap-[var(--spacing-primitives-16)] uipm:w-full"
        onSubmit={handleSubmit}
        noValidate
        autoComplete="off"
      >
        <input
          name="oid"
          type="hidden"
          value={process.env.NEXT_PUBLIC_SALESFORCE_ORG_ID}
        />
        <input
          name="retURL"
          type="hidden"
          value={`${process.env.NEXT_PUBLIC_SALESFORCE_RETURN_URL}/contact-us/thank-you`}
        />
        <input
          name="debugEmail"
          type="hidden"
          value={process.env.NEXT_PUBLIC_SALESFORCE_DEBUG_EMAIL}
        />
        <input type="hidden" name="lead_source" value="Website - Get a Demo" />

        <div className="uipm:grid uipm:grid-cols-1 uipm:md:grid-cols-2 uipm:gap-[var(--spacing-primitives-16)]">
          <Input
            id="first_name"
            name="first_name"
            type="text"
            placeholder="First Name*"
            maxLength={40}
            background="white"
            state={formErrors.first_name ? "error" : "default"}
            value={formValues.first_name || ""}
            onChange={(e) => handleFieldChange("first_name", e.target.value)}
            onFocus={() => handleFieldFocus("first_name")}
          />
          <Input
            id="last_name"
            name="last_name"
            type="text"
            placeholder="Last Name*"
            maxLength={80}
            background="white"
            state={formErrors.last_name ? "error" : "default"}
            value={formValues.last_name || ""}
            onChange={(e) => handleFieldChange("last_name", e.target.value)}
            onFocus={() => handleFieldFocus("last_name")}
          />
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="Email*"
            maxLength={80}
            background="white"
            state={formErrors.email ? "error" : "default"}
            value={formValues.email || ""}
            onChange={(e) => handleFieldChange("email", e.target.value)}
            onFocus={() => handleFieldFocus("email")}
          />
          <Input
            id="phone"
            name="phone"
            type="tel"
            placeholder="Phone*"
            maxLength={40}
            background="white"
            state={formErrors.phone ? "error" : "default"}
            value={formValues.phone || ""}
            onChange={(e) => handleFieldChange("phone", e.target.value)}
            onFocus={() => handleFieldFocus("phone")}
          />
        </div>

        <div className="uipm:w-full">
          <Input
            id="company"
            name="company"
            type="text"
            placeholder="Company*"
            maxLength={40}
            background="white"
            state={formErrors.company ? "error" : "default"}
            value={formValues.company || ""}
            onChange={(e) => handleFieldChange("company", e.target.value)}
            onFocus={() => handleFieldFocus("company")}
          />
        </div>

        <div className="uipm:grid uipm:grid-cols-1 uipm:md:grid-cols-2 uipm:gap-[var(--spacing-primitives-16)]">
          <div
            className="uipm:w-full"
            onFocus={() => handleFieldFocus("country_code")}
          >
            <Select
              placeholder="Country"
              value={countryCode}
              background="white"
              state={formErrors.country_code ? "error" : "default"}
              onValueChange={(value) => {
                setCountryCode(value);
                handleFieldChange("country_code", value);
              }}
            >
              <option value="CA">Canada</option>
              <option value="US">United States</option>
            </Select>
            <input type="hidden" name="country_code" value={countryCode} />
          </div>
          <div
            className="uipm:w-full"
            onFocus={() => handleFieldFocus("state_code")}
          >
            <Select
              placeholder="State/Province"
              value={stateCode}
              background="white"
              state={formErrors.state_code ? "error" : "default"}
              onValueChange={(value) => {
                setStateCode(value);
                handleFieldChange("state_code", value);
              }}
            >
              {STATE_PROVINCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <input type="hidden" name="state_code" value={stateCode} />
          </div>
        </div>

        <div
          className="uipm:w-full"
          onFocus={() => handleFieldFocus("industry")}
        >
          <Select
            placeholder="Industry"
            value={industry}
            background="white"
            state={formErrors.industry ? "error" : "default"}
            onValueChange={(value) => {
              setIndustry(value);
              handleFieldChange("industry", value);
            }}
          >
            <option value="Airport">Airport</option>
            <option value="Commercial">Commercial</option>
            <option value="Education">Education</option>
            <option value="Event">Event</option>
            <option value="Municipal">Municipal</option>
            <option value="Operator">Operator</option>
            <option value="Fleet">Fleet</option>
            <option value="Technology Partner">Technology Partner</option>
          </Select>
          <input type="hidden" name="industry" value={industry} />
        </div>

        <div
          className="uipm:w-full"
          onFocus={() =>
            handleFieldFocus(
              process.env.NEXT_PUBLIC_SALESFORCE_FIELD_PRODUCT || "product",
            )
          }
        >
          <Select
            placeholder="Product"
            value={product}
            background="white"
            onValueChange={setProduct}
          >
            <option value="On/Off-Street Pay By App Parking">
              On/Off-Street Pay By App Parking
            </option>
            <option value="Garage Reservations">Garage Reservations</option>
            <option value="Event/Venue Reservations">
              Event/Venue Reservations
            </option>
            <option value="Corporate Account">Corporate Account</option>
            <option value="Permits">Permits</option>
            <option value="Electric Vehicle Charging">
              Electric Vehicle Charging
            </option>
            <option value="Customer Service Inquiry">
              Customer Service Inquiry
            </option>
            <option value="Insights">Insights</option>
            <option value="Not Sure">Not Sure</option>
          </Select>
          <input
            type="hidden"
            name={process.env.NEXT_PUBLIC_SALESFORCE_FIELD_PRODUCT}
            value={product}
          />
        </div>

        <div className="uipm:w-full">
          <Textarea
            id={process.env.NEXT_PUBLIC_SALESFORCE_FIELD_MESSAGE}
            name={process.env.NEXT_PUBLIC_SALESFORCE_FIELD_MESSAGE}
            placeholder="What would you like to learn more about?"
            rows={1}
            background="white"
            onFocus={() =>
              handleFieldFocus(
                process.env.NEXT_PUBLIC_SALESFORCE_FIELD_MESSAGE || "message",
              )
            }
          />
        </div>

        {/* <div
          className="g-recaptcha"
          data-sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_KEY}
        ></div> */}

        <Button type="submit" variant="primary" className="uipm:self-start">
          Submit
        </Button>
      </form>
    </div>
  );
}
